<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import CustomButton from "@/components/Button/Button.vue";
import {AvailabilityQuery} from "@/models/AvailabilityQuery";
import {useI18n} from 'vue-i18n';
import YachtCard from "@/components/Yacht/YachtCard.vue";
import {throttle} from 'lodash';
import {useRecommendationStore} from "@/stores/recommendationStore";
import {Yacht} from "@/models/Yacht";
import {useComparisonStore} from "@/stores/comparisonStore";
import SearchResultTab from "@/pages/SearchResultsOffers/SearchResultTab.vue";
import LocationMap from "@/components/Map/LocationMap.vue";
import SpecialRequest from "@/components/SpecialRequest/SpecialRequest.vue";
import {trackEvent} from "@/services/event.service";

const recommendationStore = useRecommendationStore();
const comparisonStore = useComparisonStore();


const route = useRoute();
const router = useRouter();

const query = ref<AvailabilityQuery | null>(null);
const {t} = useI18n(); // `t` for translating strings

const isScrolled = ref(false);
const handleScroll = () => {
  isScrolled.value = window.scrollY > 75;
};
const throttledHandleScroll = throttle(handleScroll, 250);

const tabInfo = computed(() => {
  return t(`tabs.${recommendationStore.selectedTab}.title`);
});

const map = useTemplateRef("map-overlay")
const closeMapPopup = () => {
  map.value.classList.add("hidden");
}

const premiumClicked = () => {
  trackEvent('cosmosy.ga.recommendations.clickPremium', {
    event_label: 'Premium',
  });
  recommendationStore.selectedTab = 'premium';
}

const standardClicked = () => {
  trackEvent('cosmosy.ga.recommendations.clickStandard', {
    event_label: 'Standard',
  });
  recommendationStore.selectedTab = 'standard';
}

const economyClicked = () => {
  trackEvent('cosmosy.ga.recommendations.clickEconomy', {
    event_label: 'Economy',
  });
  recommendationStore.selectedTab = 'economy';
}


const displayedAmenities = computed(() => []);
const moreThanThree = computed(() => false);

const tabColors = {
  premium: '#AE8BF8',
  standard: '#6ECFBC',
  economy: '#FFC466'
};


function onCompareClick(yacht) {
  comparisonStore.onCompare(yacht);
}

function handleExpandSearch() {
  trackEvent('cosmosy.ga.searchResultsOffers.expandSearch', {
    event_label: 'Expand Search',
  })

  router.push({ path: '/search', query: route.query })
}


// TODO - Refactor this to match the search results logic
onMounted(() => {
  window.addEventListener('scroll', throttledHandleScroll);
  recommendationStore.handleRoute(route)

});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', throttledHandleScroll);
});

watch(
    route,
    (newRoute) => {
      if (route.query.dates?.length === 2) {
        query.value = {
          dateFrom: new Date(newRoute.query.dates[0]).toISOString().split("T")[0],
          dateTo: new Date(newRoute.query.dates[1]).toISOString().split("T")[0],
          minCabins: parseInt(newRoute.query.cabins as string ?? "0", 10),
          maxCabins: parseInt(newRoute.query.cabins as string ?? "0", 10),
          minBerths: parseInt(newRoute.query.passengers as string ?? "0", 10),
          yachtModelIds: newRoute.query.yachtModel ? [parseInt(route.query.yachtModel as string ?? "0", 10)] : [],
          yachtCategoryIds: newRoute.query.type ? [parseInt(route.query.type as string ?? "0", 10)] : [],
          regionIds: newRoute.query.regionIds || [],
          countryIds: newRoute.query.countryIds || [],
          locationIds: newRoute.query.locationIds || [],
          cityIds: newRoute.query.cityIds || [],
          charterTypeId: newRoute.query.charterTypeId || [],
          price: parseInt(newRoute.query.price as string ?? "0", 10),
        };

      }
    }
)


</script>

<template>
  <div
      class="sticky w-full z-20"
      :class="isScrolled ? 'top-18' : 'top-14'"
  >
    <div class="h-fit bg-background-light pt-8 pb-1"
         :class="{'pt-0!important': isScrolled }">
      <div class="container flex justify-between h-fit">
        <span class="search-results__page-title font-semibold responsive-title-6">
              {{ t('searchResults.pageTitle') }} </span>
        <div class="flex flex-row gap-5 justify-end boat-result__buttons">
          <div class="cursor-pointer flex flex-row items-center h-7.5 gap-2 select-none"
               @click="handleExpandSearch">
            <img src="@/assets/icons/svg/Filters_24x24.svg" alt="advanced"/>
            <span class="font-semibold">{{ t('searchResults.advancedSearch') }}</span>
          </div>
        </div>
      </div>
      <div class="container flex justify-between 2xl:gap-x-6 gap-x-6 relative align-center pt-4 pb-7 tabs overflow-x-scroll overflow-y-hidden md:overflow-x-clip">

        <SearchResultTab
          selector = "premium"
          :title=" t('premium')"
          :scrolled = "isScrolled"
          :color = "tabColors.premium"
          :bundle = "recommendationStore.premium"
          :selected = "recommendationStore.selectedTab === 'premium'"
          @on-click = "premiumClicked"
        />

        <SearchResultTab
          selector = "standard"
          :title=" t('standard')"
          :selected = "recommendationStore.selectedTab === 'standard'"
          :scrolled = "isScrolled"
          :color = "tabColors.standard"
          :bundle = "recommendationStore.standard"
          @on-click = "standardClicked"
        />

        <SearchResultTab
          selector = "economy"
          :title=" t('economy')"
          :selected = "recommendationStore.selectedTab === 'economy'"
          :scrolled = "isScrolled"
          :color = "tabColors.economy"
          :bundle = "recommendationStore.economy"
          @on-click = "economyClicked"
        />
      </div>
    </div>
  </div>


  <div class="container pt-6">
    <div class="search-results-body__title items-center flex justify-space-between">
      <div class="flex justify-between search-results-body__text">
        <span class="hidden responsive-title-6 font-semibold sm:block ">{{ tabInfo }}</span>
        <div class="flex flex-row gap-5">
          <div><strong>{{ recommendationStore.getSelectedBundleCount }}</strong>
              {{ t('global.ui.yachts') }}
          </div>
        </div>
      </div>
      <div class="flex flex-row gap-5 justify-end sm:hidden w-full">
        <div
            class="cursor-pointer flex flex-row items-center gap-2 select-none ml-auto"
            @click="$router.push({ path: '/search', query: route.query })">
          <img src="@/assets/icons/svg/Filters_16x16.svg" alt="advanced"/>
          <span class="font-semibold">{{ t('searchResults.advancedSearch') }}</span>
        </div>
      </div>
    </div>


    <div v-if="recommendationStore.getSelectedBundleCount !=0 && !recommendationStore.isLoading"
         class="boat-result-container grid 2xl:grid-cols-4 grid-cols-3 gap-6 pt-8">

      <YachtCard
          @on-compare-click="val => { onCompareClick(val); }"
          @on-view-details-click="(val) => { router.push({path: `/yachts/${val}`, query: { ...route.query } })}"
          :yachts="recommendationStore.getSelectedBundle"
      />
      <!--Can't find -->
      <div class="flex flex-col gap-2 card">
        <div class="flex flex-col justify-center items-center h-full gap-4">
          <img src="/assets/images/search-results.svg" alt="back" class="w-40">
          <div class="font-bold responsive-title-5 text-center">{{ t('searchResults.cantFind') }}</div>
          <div class=" responsive-body-medium text-center">{{ t('searchResults.sendInquiry') }}</div>

          <CustomButton
              @click="$router.push({path: '/search', query: route.query})"
              :rounded="true"
              :outlined="true"
              class="w-fit text-semibold hover:text-white hover:bg-black hover:border-black">Expand
            Search
          </CustomButton>
          <SpecialRequest :t="t" :showSpecialRequestText="false" />
        </div>
      </div>
    </div>
    <div v-else-if="recommendationStore.isLoading">
      <el-container v-loading="true" style="width: 100%">
        <div class="boat-result-container grid 2xl:grid-cols-4 grid-cols-3 gap-6 pt-8 w-full">
          <div v-for="index in 6" :key="index" class="border width-fill rounded-8xl flex flex-col gap-2 card">
            <div
                class="flex flex-col gap-4 bg-gradient-to-r from-gradients-ghostWhite to-gradients-aliceBlue rounded-8xl p-5 min-h-[18.75rem]">
              <div class="flex flex-col gap-2">
                <div class="flex justify-between items-center">
                  <div class=" flex flex-col gap-2 w-3/6">
                    <div class="flex gap-1">
                      <img src="@/assets/icons/svg/Location_16x16.svg" alt="location"/>
                      <div>
                        <el-skeleton :rows="0"/>
                      </div>
                    </div>
                  </div>
                  <div class="flex gap-2">
                    <button :class="`border px-4 py-1.5 rounded-12xl h-fit`">
                      <img src="@/assets/icons/svg/Comparison_16x16.svg" alt="comparison"/>
                    </button>
                    <div class="border px-4 py-1.5 rounded-12xl h-fit">
                      <img src="@/assets/icons/svg/heart-icon.svg" alt="add to trip"/>
                    </div>
                  </div>
                </div>
                <div class="flex gap-1">
                  <el-skeleton animated :rows="0"/>
                  •
                  <el-skeleton animated :rows="0"/>
                </div>
              </div>
              <div class="boat-content flex flex-row items-center">
                <div class="boat-image-content h-full w-3/5">
                  <div class="absolute boat-image-content__icon">
                    <img src="@/assets/icons/svg/Boat_plan_24x24.svg" alt="image"/>
                  </div>

                  <el-skeleton-item variant="image" class="rounded-8xl ship-bg-mask h-full object-cover boatImage"/>
                </div>
                <div class="flex flex-col gap-2 ml-auto justify-between w-3/10 boat-stats-container">
                  <div class="flex flex-row items-center gap-2">
                    <img src="@/assets/icons/svg/Bed_24x24.svg" alt="beds"/>
                    <span style="font-size:14px"><el-skeleton animated :rows="0"/></span>
                  </div>
                  <div class="flex flex-row items-center gap-2">
                    <img src="@/assets/icons/svg/Cabin_24x24.svg" alt="cabins"/>
                    <span style="font-size:14px"><el-skeleton animated :rows="0"/></span>
                  </div>
                  <div class="flex flex-row items-center gap-2">
                    <img src="@/assets/icons/svg/WC_24x24.svg" alt="bathroom"/>
                    <span style="font-size:14px"><el-skeleton animated :rows="0"/></span>
                  </div>
                  <div class="flex flex-row items-center gap-2">
                    <img src="@/assets/icons/svg/Length_24x24.svg" alt="length"/>
                    <span style="font-size:14px"><el-skeleton animated :rows="0"/></span>
                  </div>
                  <div class="flex flex-row items-center gap-2">
                    <img src="@/assets/icons/svg/Engine_24x24.svg" alt="engine"/>
                    <span style="font-size:14px"><el-skeleton animated :rows="0"/></span>
                  </div>
                </div>
              </div>
              <div class="flex flex-row gap-3 flex-wrap">
                <div v-for="(amenity, index) in displayedAmenities" :key="index"
                     class="bg-bw-silver px-3 rounded-15xl">
                  <span style="font-size:12px"><el-skeleton animated :rows="0"/></span>
                </div>
                <div v-if="moreThanThree" class="bg-bw-silver px-3 rounded-15xl">
                  <span style="font-size:12px">+ <el-skeleton animated :rows="0"/> more</span>
                </div>
              </div>
            </div>
            <div class="flex place-content-between px-5 py-2.5">
              <div class="w-max">
                <div class="flex flex-row gap-1 items-center">
                  <el-skeleton animated :rows="0"/>
                </div>
              </div>
              <div class="flex flex-row items-center justify-between">
                <button class="flex flex-row items-center gap-1 bg-black text-white rounded-12xl px-5 py-2">
                  {{ t('boatDetails.viewDetails') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </el-container>
    </div>
    <div v-else>
      <div class="boat-result-container grid 2xl:grid-cols-4 grid-cols-3 gap-6 pt-8">
        <div class="flex flex-col gap-2 card">
          <div class="flex flex-col justify-center items-center h-full gap-4">
            <img src="/assets/images/search-results.svg" alt="back" class="w-40">
            <div class="font-bold responsive-title-5">Can't find what you want?</div>
            <div class=" responsive-body-medium text-center"> Use the expanded search, or let our brokers assist you.</div>
            <CustomButton 
                @click="$router.push({path: '/search', query: route.query})"
                :rounded="true"
                :outlined="true"
                 class="w-fit text-semibold hover:text-white hover:bg-black hover:border-black">Expand
              Search
            </CustomButton>
            <SpecialRequest :t="t" :showSpecialRequestText="false" />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!--TODO 2025-01-18  this should be removed in the future-->

  <div id="map-overlay" ref="map-overlay" class="map-popup-overlay hidden" @click="closeMapPopup">
    <div id="map-overlay-map" class="map-popup-map relative" @click.stop>
      <div @click="closeMapPopup"
           class="cursor-pointer flex items-center justify-center z-50 absolute w-6 h-6 top-8 right-8 border border-black rounded-full p-1.5">
        <img src="@/assets/icons/svg/Close_16x16.svg" alt="">
      </div>
    </div>
  </div>

</template>


<style scoped>
@media (min-width: 768px) {
  .tabs {
    overflow-x: clip !important;
  }
}
:deep(.tabs .el-skeleton) {
  width: 5vw;
}

:deep(.tabs .el-skeleton__item.el-skeleton__p ) {
  width: 4.5vw;
}

:deep(.boat-result-container .el-skeleton) {
  width: 8vw;
}

:deep(.boat-result-container .el-skeleton__item.el-skeleton__p ) {
  width: 6.5vw;
}

:deep(.boat-stats-container .el-skeleton) {
  width: 6vw;
}

:deep(.boat-stats-container .el-skeleton__item.el-skeleton__p ) {
  width: 4.5vw;
}

:deep(.el-loading-mask) {
  @apply z-20;
}

.border-black {
  border-color: #000 !important;
}

.trip-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.trip-popup {
  width: 80%;
  height: 80%;
  background-color: white;
  border-radius: 10px;
  position: relative;
}

.map-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.map-popup-map {
  width: 80%;
  height: 80%;
  background-color: white;
  border-radius: 10px;
  position: relative;
}

.hidden {
  display: none;
}

.notification-bubble {
  background-color: #000000; /* Bubble background color */
  color: white; /* Text color */
  font-size: 12px; /* Text size */
  width: 20px; /* Width of the bubble */
  height: 20px; /* Height of the bubble */
  border-radius: 50%; /* Make the bubble round */
  position: absolute; /* Positioning relative to the parent */
  right: 0px; /* Distance from the right edge of the parent */
  top: -12px; /* Positioning the bubble slightly above the top edge */
}

.bg-premium {
  background-color: #AE8BF8;
  color: black;
}

.bg-standard {
  background-color: #6ECFBC;
  color: black;
}

.bg-economy {
  background-color: #FFC466;
  color: black;
}

.border-primary-blue {
  border-color: #3B82F6 !important;
}

.boat-result-mob__buttons button:not(button.border-primary-blue):hover {
  border-color: #000 !important;
}

/* New CSS for boat card layout in three columns */
.flex.flex-row.flex-wrap {
  display: flex;
  flex-wrap: wrap;
}

.card {
  display: flex;
  flex-direction: column;
}

.tab {
  padding: 20px;
  cursor: pointer;
  position: relative;
}


.tabs:has(.tab.scrolled) {
  padding-top: 2rem !important;
}

.tab.scrolled {
  gap: .5rem;
}

.tab.scrolled img {
  width: 1.875rem !important;
}

.tab.scrolled .tab-second-row {
  display: none;
}

.tab.scrolled .tab-category__title {
  font-size: 1.25rem;
}

.tab.scrolled .tabs-from {
  opacity: 0;
  display: none;
}

.tab.scrolled .tab-category__min-price {
  font-size: 1rem;
}

.category-title__text.scrolled {
  padding-top: 1rem !important;
}

.ship-bg-mask {
  mask-image: url("@/assets/images/mask/mask.svg");
  mask-repeat: no-repeat;
  mask-size: cover;
}

.boat-image-content__icon {
  width: 24px;
  height: 24px;
  padding-right: 2px;
}

.boatImage {
  margin-left: 3px;
  margin-top: 3px;
}

@media (max-width: 1575px) {
  .boat-result-container {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1165px) {
  .boat-result-container {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .img-data {
    column-gap: 1.5rem
  }
}

@media (min-width: 1140px) {
  .boatImage-container {
    width: 32%
  }

  .boat-result-mob__buttons {
    width: 32%;
  }
}

@media (max-width: 1165px) and (min-width: 1051px) {
  .boat-image-content {
    width: 85% !important;
  }

  .boat-content-header {
    width: 85% !important;
  }
}

@media (max-width: 1165px) {
  .boatImage-container {
    width: 35%
  }

  .boat-result-mob__buttons {
    width: 35%;
  }
}

@media (max-width: 1024px) {
  :deep(.tabs .el-skeleton__item.el-skeleton__p ) {
    width: 5.8vw;
  }
}

@media (min-width: 886px) {
  .left-tab.selected-tab {
    transform-origin: left;
  }

  .right-tab.selected-tab {
    transform-origin: right;
  }

  .tabs .tab:not(.selected-tab) {
    transform: scale(.95);
  }

  .tab.selected-tab::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -25px;
    width: 100%;
    height: 5px;
    border-radius: 5px;
    background-color: var(--tab-after-color);
  }

  .search-results-body__text {
    width: 100%;
  }
}

@media (max-width: 885px) {
  :deep(.tabs .el-skeleton__item.el-skeleton__p ) {
    width: 8vw;
  }
  .tabs:has(.tab.scrolled) {
    padding-top: 3.5rem !important;
  }

  .search-results__page-title {
    font-size: 1.25rem;
  }


  .search-results-body__button {
    display: flex;
  }

  .search-results-body__title {
    flex-direction: row;
  }

  .search-results-body__text {
    flex-direction: column;
  }

  .boat-result__buttons {
    display: none;
  }

  .tabs .tab:not(.selected-tab) {
    transform: scale(.93);
  }
}

@media (max-width: 875px) {
  .search-results-body__mobile-buttons {
    display: flex;
  }
}

@media (max-width: 768px) {
  :deep(.tabs .el-skeleton__item.el-skeleton__p ) {
    width: 10vw;
  }

  .boat-result-container {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }

  .boat-image-content {
    width: 80%

  }
}

@media (max-width: 400px) {
  :deep(.tabs .el-skeleton__item.el-skeleton__p ) {
    width: 20vw;
  }

  .price-offer-days {
    display: none;
  }
}

.left-tab.selected-tab {
  transform-origin: left;
}

.right-tab.selected-tab {
  transform-origin: right;
}

.tabs .tab:not(.selected-tab) {
  transform: scale(.95);
}

.tab.selected-tab::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -25px;
  width: 100%;
  height: 5px;
  border-radius: 5px;
  background-color: var(--tab-after-color);
}

.search-results-body__text {
  width: 100%;
}
</style>